import React from 'react';

interface CrossDepartmentCollaborationMatrixProps {
  data: {
    matrix: Array<{
      source_department: string;
      target_department: string;
      collaboration_score: number;
      joint_workshops: number;
      knowledge_sharing: number;
      cross_training: number;
    }>;
    departments: string[];
    insights: {
      top_collaborators: Array<{
        department: string;
        collaboration_score: number;
        partnerships: number;
      }>;
      silo_breakers: Array<{
        department: string;
        cross_department_initiatives: number;
        impact_score: number;
      }>;
      collaboration_gaps: Array<{
        department_pair: string;
        potential_score: number;
        opportunity: string;
      }>;
    };
  };
  loading?: boolean;
}

const CrossDepartmentCollaborationMatrix: React.FC<CrossDepartmentCollaborationMatrixProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Cross-Department Collaboration Matrix</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Cross-Department Collaboration Matrix</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No collaboration data available
        </div>
      </div>
    );
  }

  const getCollaborationLevel = (score: number) => {
    if (score >= 80) return { level: 'Excellent', color: '#10b981' };
    if (score >= 60) return { level: 'Good', color: '#3b82f6' };
    if (score >= 40) return { level: 'Average', color: '#f59e0b' };
    if (score >= 20) return { level: 'Low', color: '#ef4444' };
    return { level: 'Minimal', color: '#6b7280' };
  };

  const getIntensityColor = (score: number) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-yellow-500';
    if (score >= 20) return 'bg-orange-500';
    return 'bg-gray-300';
  };

  // Create matrix data
  const matrixData = data.departments.map(sourceDept => {
    const row: any = { source_department: sourceDept };
    data.departments.forEach(targetDept => {
      if (sourceDept === targetDept) {
        row[targetDept] = 100; // Self-reference (diagonal)
      } else {
        const collaboration = data.matrix.find(d => d.source_department === sourceDept && d.target_department === targetDept);
        row[targetDept] = collaboration ? collaboration.collaboration_score : 0;
      }
    });
    return row;
  });

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Cross-Department Collaboration Matrix</h3>
      
      {/* Matrix Table */}
      <div className="overflow-x-auto mb-6">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="border border-gray-300 p-2 text-xs font-medium text-gray-700 bg-gray-50"></th>
              {data.departments.map(dept => (
                <th key={dept} className="border border-gray-300 p-2 text-xs font-medium text-gray-700 bg-gray-50 text-center">
                  {dept.length > 8 ? dept.substring(0, 8) + '...' : dept}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrixData.map((row, rowIndex) => (
              <tr key={row.source_department}>
                <td className="border border-gray-300 p-2 text-xs font-medium text-gray-700 bg-gray-50">
                  {row.source_department.length > 12 ? row.source_department.substring(0, 12) + '...' : row.source_department}
                </td>
                {data.departments.map((dept, colIndex) => {
                  const score = row[dept] as number;
                  const isDiagonal = rowIndex === colIndex;
                  return (
                    <td 
                      key={`${row.source_department}-${dept}`}
                      className={`border border-gray-300 p-2 text-center text-xs font-medium ${
                        isDiagonal 
                          ? 'bg-gray-200 text-gray-500' 
                          : score > 0 
                            ? 'text-white' 
                            : 'text-gray-500'
                      } ${!isDiagonal ? getIntensityColor(score) : ''}`}
                      title={`${row.source_department} → ${dept}: ${score.toFixed(1)}% collaboration`}
                    >
                      {isDiagonal ? '—' : score.toFixed(0)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Insights Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Top Collaborators */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Top Collaborators</h4>
          <div className="space-y-2">
            {data.insights.top_collaborators.map((item, index) => {
              const collaboration = getCollaborationLevel(item.collaboration_score);
              return (
                <div key={item.department} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center">
                    <span className="text-sm font-medium mr-2">#{index + 1}</span>
                    <span className="text-sm font-medium">{item.department}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{item.partnerships}</span> partnerships
                    <span className={`ml-2 text-xs px-2 py-1 rounded`} style={{ 
                      backgroundColor: collaboration.color + '20', 
                      color: collaboration.color 
                    }}>
                      {collaboration.level}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Silo Breakers */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Silo Breakers</h4>
          <div className="space-y-2">
            {data.insights.silo_breakers.map((item, index) => (
              <div key={item.department} className="p-2 bg-green-50 rounded">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-green-800">{item.department}</span>
                  <span className="text-sm font-bold text-green-600">{item.cross_department_initiatives}</span>
                </div>
                <div className="text-xs text-green-600 mt-1">
                  Impact: {item.impact_score.toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Collaboration Gaps */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Collaboration Opportunities</h4>
          <div className="space-y-2">
            {data.insights.collaboration_gaps.map((item, index) => (
              <div key={item.department_pair} className="p-2 bg-blue-50 rounded">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-blue-800">{item.department_pair}</span>
                  <span className="text-sm font-bold text-blue-600">{item.potential_score.toFixed(1)}%</span>
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  {item.opportunity}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Collaboration Intensity Legend</h4>
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
            <span className="text-gray-600">Excellent (80-100%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
            <span className="text-gray-600">Good (60-79%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-yellow-500 rounded mr-2"></div>
            <span className="text-gray-600">Average (40-59%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-orange-500 rounded mr-2"></div>
            <span className="text-gray-600">Low (20-39%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-gray-300 rounded mr-2"></div>
            <span className="text-gray-600">Minimal (0-19%)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrossDepartmentCollaborationMatrix;


